// A danışman's round photo next to her name: the portrait the reports
// print (reportProfile.photoFileId, served by GET /api/provider/:id/photo),
// or her initials when she has none or it has not loaded. Each photo is
// asked for once per page; every avatar of the same danışman shares it.

import {useEffect, useState} from "react";
import {ApiRequestError, getProviderPhoto} from "../api/client";

const photos = new Map<string, Promise<string | null>>();

// Dev only (/?demo): a drawn stand-in, no door.
const DEMO = import.meta.env.DEV && window.location.search.includes("demo");
const DEMO_PHOTO = "data:image/svg+xml," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="#c9d8e6"/>` +
  `<circle cx="20" cy="16" r="7" fill="#7f99b3"/><ellipse cx="20" cy="36" rx="13" ry="10" fill="#7f99b3"/></svg>`);

function photoOf(providerId: string): Promise<string | null> {
  let p = photos.get(providerId);
  if (!p) {
    p = DEMO ? Promise.resolve(DEMO_PHOTO) :
      getProviderPhoto(providerId).then((r) => r.data.dataUrl, (e) => {
        // No photo stays known for the page; any other failure is
        // forgotten, so the next avatar to mount asks again.
        if (!(e instanceof ApiRequestError && e.status === 404)) photos.delete(providerId);
        return null;
      });
    photos.set(providerId, p);
  }
  return p;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toLocaleUpperCase("tr");
}

export function Avatar({providerId, name, photo, size = "sm"}: {
  providerId: string;
  name: string;
  photo?: boolean; // the board says she has one
  size?: "sm" | "lg";
}) {
  const [src, setSrc] = useState<{id: string; url: string | null} | null>(null);
  useEffect(() => {
    if (!photo) return;
    let live = true;
    void photoOf(providerId).then((url) => { if (live) setSrc({id: providerId, url}); });
    return () => { live = false; };
  }, [providerId, photo]);
  const url = photo && src?.id === providerId ? src.url : null;
  return (
    <span className={`avatar ${size}`} aria-hidden>
      {url ? <img src={url} alt="" /> : initials(name)}
    </span>
  );
}
